import { isLevelId, type LevelId } from '../data/ids';
import { loadLevel } from '../data/load-level';
import { LEVELS } from '../data/levels';
import type { ScreenFlow, ScreenId } from '../game/screen-flow';
import { t, type StringKey } from '../i18n/es';
import type { SaveStore } from '../save/save-store';
import { pauseDialog, type PauseControls } from './dialogs/pause';
import { flightHud } from './hud/hud';
import { briefingScreen } from './screens/briefing';
import { briefingModel } from './screens/briefing-model';
import { gameOverModel } from './screens/game-over-model';
import { operacionFallidaScreen } from './screens/operacion-fallida';
import { recuentoScreen } from './screens/recuento';
import { ajustesScreen } from './screens/ajustes';
import type { Settings } from '../save/schema';
import type { Recount } from '../game/recount';
import type { LevelRun } from '../core/level-runner';
import { expedientesScreen } from './screens/expedientes';
import { misionesScreen } from './screens/misiones';
import { closingScreen } from './screens/closing';

/** DOM overlay for the screens in GDD §13.2. Copy follows the design system voice (GDD §13.4). */

function aviso(onContinue: () => void): HTMLElement {
  const section = document.createElement('section');
  section.className = 'panel';
  section.setAttribute('aria-labelledby', 'aviso-title');
  section.innerHTML = `
    <p class="tactical">${t('ui.aviso.label')}</p>
    <h1 id="aviso-title" class="panel-title">${t('ui.aviso.title')}</h1>
    <p class="body">${t('ui.aviso.detail')}</p>
    <div class="rule"></div>
    <button type="button" class="button">${t('ui.aviso.continue')}</button>
  `;
  const button = section.querySelector('button')!;
  button.addEventListener('click', onContinue);
  queueMicrotask(() => button.focus());
  return section;
}

function titulo(): HTMLElement {
  const section = document.createElement('section');
  section.className = 'title';
  section.setAttribute('aria-labelledby', 'title-heading');
  section.innerHTML = `
    <p class="tactical">${t('ui.title.brand')}</p>
    <h1 id="title-heading" class="display">${t('ui.title.name')}</h1>
    <p class="tactical">${t('ui.titulo.prompt')}</p>
    <p class="badge">${t('ui.titulo.badge')}</p>
  `;
  return section;
}

const SHELL_TITLES: Partial<Record<ScreenId, StringKey>> = {
  ARRANQUE: 'ui.screen.arranque',
};

/** Placeholder with the tactical title of a screen, so the flow is navigable until its issue lands. */
function shell(screen: ScreenId, key: StringKey): HTMLElement {
  const section = document.createElement('section');
  section.className = 'panel';
  section.dataset.shell = screen;
  section.innerHTML = `<p class="tactical">${t(key)}</p>`;
  return section;
}

/** Keys and taps that take off from TITULO ("Pulse para despegar"). */
const START_KEYS = new Set(['Space', 'Enter']);

export interface ScreenServices {
  /** The save store of the game (#46), read fresh on every render. */
  store: () => SaveStore;
  /** "Confirmar" in BRIEFING: loads the level (from the saved checkpoint when it is in progress), #57. */
  deploy: (levelId: LevelId) => void;
  /** The pause of the flight (#60): the button of VUELO and the choices of PAUSA. */
  flight: PauseControls & { pause(): void };
  /** The run of the level in flight, read by OPERACIÓN FALLIDA after the core restored it (#61). */
  run: () => LevelRun | null;
  /** The recount of the level just completed (#62). */
  recount: () => Recount | null;
  /** AJUSTES (#63): writes one setting and tells the audio, the FPS counter and the interface motion. */
  updateSetting: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
}


/** The BRIEFING of a campaign level, from its data when it is built (GDD §10.1) and the active slot. */
function briefingFor(levelId: LevelId, store: SaveStore) {
  const level = LEVELS[levelId] ? loadLevel(levelId) : null;
  const save = store.save;
  const slot = save.lastSlotId ? save.slots[save.lastSlotId] : null;
  return briefingModel(levelId, level, slot?.current.levelId === levelId ? slot.current.checkpointId : null);
}

export function mountScreens(root: HTMLElement, flow: ScreenFlow, services: ScreenServices): void {
  const render = (screen: ScreenId) => {
    root.dataset.screen = screen;
    if (screen === 'AVISO') root.replaceChildren(aviso(() => flow.continue()));
    else if (screen === 'TITULO') root.replaceChildren(titulo());
    else if (screen === 'EXPEDIENTES') {
      root.replaceChildren(expedientesScreen({ store: services.store, onSlotReady: () => flow.send('SLOT_READY') }));
    } else if (screen === 'MISIONES') {
      root.replaceChildren(
        misionesScreen({
          store: services.store,
          onSelect: (levelId) => flow.send('SELECT_MISSION', { levelId }),
          onOpenSettings: () => flow.send('OPEN_SETTINGS'),
          onOpenCredits: () => flow.send('OPEN_CREDITS'),
        }),
      );
    } else if (screen === 'BRIEFING' && flow.levelId && isLevelId(flow.levelId)) {
      const levelId = flow.levelId;
      root.replaceChildren(
        briefingScreen({
          model: briefingFor(levelId, services.store()),
          onDeploy: () => services.deploy(levelId),
          onBack: () => flow.send('BACK'),
        }),
      );
    } else if (screen === 'VUELO') {
      // VUELO shows the scene and the HUD (#58) over it.
      root.replaceChildren(
        flightHud({ levelId: () => flow.levelId, run: () => services.run(), onPause: () => services.flight.pause() }),
      );
    } else if (screen === 'PAUSA') {
      root.replaceChildren(pauseDialog(services.flight));
    } else if (screen === 'AJUSTES') {
      root.replaceChildren(
        ajustesScreen({
          settings: () => services.store().save.settings,
          update: (key, value) => services.updateSetting(key, value),
          onBack: () => flow.send('BACK'),
        }),
      );
    } else if (screen === 'RECUENTO' && services.recount()) {
      root.replaceChildren(
        recuentoScreen({
          recount: services.recount()!,
          reducedMotion: services.store().save.settings.reducedMotion,
          onContinue: () => flow.send('CONTINUE'),
        }),
      );
    } else if (screen === 'CIERRE' && flow.closing) {
      root.replaceChildren(
        closingScreen({
          sequence: flow.closing,
          reducedMotion: services.store().save.settings.reducedMotion,
          onFinish: () => flow.send('CONTINUE'),
        }),
      );
    } else if (screen === 'OPERACION_FALLIDA' && flow.levelId && isLevelId(flow.levelId)) {
      root.replaceChildren(operacionFallidaScreen(gameOverModel(flow.levelId, services.run()), () => flow.send('RETRY')));
    } else {
      const key = SHELL_TITLES[screen];
      root.replaceChildren(...(key ? [shell(screen, key)] : []));
    }
  };
  flow.subscribe(render);
  render(flow.current);

  window.addEventListener('keydown', (event) => {
    if (flow.current !== 'TITULO' || !START_KEYS.has(event.code)) return;
    event.preventDefault();
    flow.send('START');
  });
  window.addEventListener('pointerdown', () => {
    if (flow.current === 'TITULO') flow.send('START');
  });
}
